import {
  Alert,
  AlertIcon,
  Box,
  CloseButton,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import CommonInput from 'components/commonInput/CommonInput';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import GoogleAddressAutocomplete from 'components/googlePlaces/GoogleAddressAutocomplete';
import TaxNumberValidationNotice from 'components/legalFields/TaxNumberValidationNotice';
import { useAddressGeocodeCheck } from 'hooks/useAddressGeocodeCheck';
import { useDebounce } from 'hooks/useDebounce';
import { usePostcodeLocationLookup } from 'hooks/usePostcodeLocationLookup';
import { useTaxNumberValidation } from 'hooks/useTaxNumberValidation';
import { getCountries, getProvinces, getCities } from 'localized-regions';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller } from 'react-hook-form';
import { formatPostalCode, isValidPostalCode } from 'utils/PostalCode';
import { hasFieldValue } from 'utils/CommonValidation';
import { TIN_VERDICT } from 'utils/taxValidationApi';

// RAT-1861: shared Legal/Address block, extracted out of the Merchant Legal
// section (RAT-1817/1822/1823/1824/1825/1788) so Organization and Seller can
// reuse the exact same rules instead of re-implementing them. Talks to the
// host form purely through props (control/register/watch/setValue/errors +
// a fieldNames adapter) - it never reads/writes Redux or calls an API
// directly, so swapping the data source behind it needs no change here.
//
// RAT-1931 (the RAT-1757 follow-up, Option 2) lives here too: Country +
// Postcode auto-fills Province and City. Implemented once in this component so
// the Merchant Legal section and the Seller/Organization Address section both
// get it - neither host form needed a change.

const DEFAULT_FIELD_NAMES = {
  legalName: 'legalName',
  taxId: 'NIF',
  vatNumber: 'vatNumber',
  addressLine1: 'legalAddressLine1',
  addressLine2: 'legalAddressLine2',
  zipCode: 'legalZipCode',
  country: 'legalCountry',
  province: 'legalProvince',
  city: 'legalCity',
  taxNumberConfirmed: 'taxNumberConfirmed',
};

// Resolve a stored name to a matching option; fall back to a synthetic option
// so existing/custom values (e.g. legacy free-text province names) still
// display and stay editable. Native-language values (opt.localized) are
// matched alongside the English name (opt.name) - RAT-1788.
// RAT-1967: bail out on an empty stored value before matching. With an English
// locale every option has `localized: undefined`, so an unset field would match
// the first option (Afghanistan) on `opt.localized === storedName`.
const resolveLegalOption = (opts, storedName) => {
  if (!storedName) return null;
  return (
    opts.find(
      (opt) => opt.name === storedName || opt.localized === storedName,
    ) || { name: storedName, code: storedName, label: storedName }
  );
};

const LegalFieldsSection = ({
  control,
  register,
  watch,
  setValue,
  errors,
  fieldNames: fieldNamesProp,
  requiredFieldNames,
  isRequired,
  textColor,
  brandStars,
  taxIdLabel = 'Tax ID',
  locale = 'en',
  isSpainProvince = false,
  showLegalNameAndTax = true,
  initialCountryCode = '',
  initialProvinceCode = '',
  onCountryCodeChange,
  children,
}) => {
  const fieldNames = useMemo(
    () => ({ ...DEFAULT_FIELD_NAMES, ...fieldNamesProp }),
    [fieldNamesProp],
  );

  const [countryIso, setCountryIso] = useState(initialCountryCode || '');
  const [provinceIso, setProvinceIso] = useState(initialProvinceCode || '');

  // Re-seed the ISO state once, when edit-mode data arrives asynchronously
  // after the initial render (matches MerchantAddEdit's hydration-on-load).
  const hasHydrated = useRef(false);
  // RAT-1931: the Country|Postcode pair the Province/City on screen already
  // correspond to. Guards AC3 (no re-fire while the pair is unchanged) and, on
  // edit-mode load, stops a saved record's Province/City being overwritten by a
  // lookup nobody asked for - AC4 only overwrites on an actual change.
  const lastLookupKeyRef = useRef(null);
  useEffect(() => {
    if (hasHydrated.current) return;
    if (!initialCountryCode && !initialProvinceCode) return;
    hasHydrated.current = true;
    setCountryIso(initialCountryCode || '');
    setProvinceIso(initialProvinceCode || '');
    // Both host forms set the zip code in the same tick they hand over the ISO
    // codes, so the stored postcode is already readable here.
    const storedZipCode = (watch(fieldNames.zipCode) || '').trim();
    if (initialCountryCode && storedZipCode) {
      lastLookupKeyRef.current = `${initialCountryCode}|${storedZipCode}`;
    }
  }, [initialCountryCode, initialProvinceCode, watch, fieldNames]);

  const countryOptions = useMemo(() => getCountries({ locale }), [locale]);
  const provinceOptions = useMemo(
    () => (countryIso ? getProvinces(countryIso) : []),
    [countryIso],
  );
  const cityOptions = useMemo(
    () => (countryIso && provinceIso ? getCities(countryIso, provinceIso) : []),
    [countryIso, provinceIso],
  );

  const watchedCountry = watch(fieldNames.country);
  const watchedProvince = watch(fieldNames.province);

  // RAT-1823: every required field in the block turns required together as
  // soon as one of them holds a value.
  const requiredWatchValues = watch(requiredFieldNames);
  const isSectionRequired =
    isRequired ?? requiredWatchValues.some(hasFieldValue);

  // RAT-1825: real-time "this doesn't look like a known address" warning.
  const watchedAddressLine1 = watch(fieldNames.addressLine1);
  const watchedZipCode = watch(fieldNames.zipCode);
  const watchedCity = watch(fieldNames.city);
  const { checkAddressMatch } = useAddressGeocodeCheck();
  const [showAddressWarning, setShowAddressWarning] = useState(false);
  const [isAddressWarningDismissed, setIsAddressWarningDismissed] =
    useState(false);
  const lastCheckedAddressRef = useRef(null);
  const addressCandidate = useMemo(() => {
    const parts = [
      watchedAddressLine1,
      watchedZipCode,
      watchedCity,
      watchedCountry,
    ];
    return parts.every(hasFieldValue)
      ? `${watchedAddressLine1}, ${watchedZipCode} ${watchedCity}, ${watchedCountry}`
      : null;
  }, [watchedAddressLine1, watchedZipCode, watchedCity, watchedCountry]);
  const debouncedAddressCandidate = useDebounce(addressCandidate, 600);

  useEffect(() => {
    setIsAddressWarningDismissed(false);

    if (!debouncedAddressCandidate) {
      setShowAddressWarning(false);
      lastCheckedAddressRef.current = null;
      return;
    }

    if (lastCheckedAddressRef.current === debouncedAddressCandidate) {
      return;
    }
    lastCheckedAddressRef.current = debouncedAddressCandidate;

    let isCurrent = true;
    checkAddressMatch(debouncedAddressCandidate).then(({ matched }) => {
      if (isCurrent) {
        setShowAddressWarning(!matched);
      }
    });

    // eslint-disable-next-line consistent-return
    return () => {
      isCurrent = false;
    };
  }, [debouncedAddressCandidate, checkAddressMatch]);

  // RAT-1911: the tax registry check for the Tax ID / VAT Number pair. Runs on
  // blur, and any edit to the two numbers or the legal name drops the previous
  // answer so a stale verdict can never be read as current.
  const watchedTaxId = watch(fieldNames.taxId);
  const watchedVatNumber = watch(fieldNames.vatNumber);
  const watchedLegalName = watch(fieldNames.legalName);
  const {
    result: taxValidation,
    isChecking: isCheckingTaxNumber,
    check: checkTaxNumber,
    reset: resetTaxNumberCheck,
  } = useTaxNumberValidation();

  const isTaxNumberConfirmed = !!watch(fieldNames.taxNumberConfirmed);
  // Guarded: this runs on every keystroke in the two tax fields, and the flag
  // is watched, so writing it unconditionally would re-render the whole block
  // per character.
  const clearTaxNumberConfirmation = useCallback(() => {
    if (isTaxNumberConfirmed) {
      setValue(fieldNames.taxNumberConfirmed, false, { shouldDirty: false });
    }
  }, [isTaxNumberConfirmed, setValue, fieldNames]);

  const handleTaxNumberBlur = useCallback(() => {
    checkTaxNumber({
      taxId: watchedTaxId,
      vatNumber: watchedVatNumber,
      legalName: watchedLegalName,
      countryCode: countryIso,
      country: watchedCountry,
    }).then((outcome) => {
      // A fresh verdict always starts unconfirmed: confirming INVALID is a
      // decision about one specific number, never a sticky form setting.
      if (outcome?.verdict !== TIN_VERDICT.INVALID) {
        clearTaxNumberConfirmation();
      }
    });
  }, [
    checkTaxNumber,
    watchedTaxId,
    watchedVatNumber,
    watchedLegalName,
    countryIso,
    watchedCountry,
    clearTaxNumberConfirmation,
  ]);

  const handleTaxNumberChanged = useCallback(() => {
    if (taxValidation) {
      resetTaxNumberCheck();
    }
    clearTaxNumberConfirmation();
  }, [taxValidation, resetTaxNumberCheck, clearTaxNumberConfirmation]);

  const adoptRegistryLegalName = useCallback(
    (registryLegalName) => {
      setValue(fieldNames.legalName, registryLegalName, {
        shouldValidate: true,
        shouldDirty: true,
      });
      // The name is what AEAT graded, so the old verdict no longer applies.
      handleTaxNumberChanged();
    },
    [setValue, fieldNames, handleTaxNumberChanged],
  );

  // RAT-1931: Country + Postcode -> Province + City.
  //  - AC2: nothing fires until both have a value.
  //  - AC3: re-fires whenever either changes, never while the pair is unchanged.
  //  - AC4: a re-fire overwrites both unconditionally; a manual edit is discarded.
  //  - AC6/AC7: empty, invalid, no-match, API error or timeout all degrade
  //    silently to plain manual entry - no error, form stays submittable.
  // AC8's styling of auto-filled values is deliberately not implemented: the
  // Province/City inputs keep the same look as every other field in the form.
  const { lookupPostcodeLocation } = usePostcodeLocationLookup();
  const debouncedZipCode = useDebounce(watchedZipCode, 500);

  useEffect(() => {
    const zipCode = (debouncedZipCode || '').trim();
    // AC2 - both halves of the pair are needed. A country typed in freehand has
    // no ISO code, so there is nothing to restrict the lookup to either.
    if (!countryIso || !zipCode) return undefined;
    // AC6 - a postcode that can't be valid for this country is not worth a call.
    if (!isValidPostalCode(zipCode, watchedCountry)) return undefined;

    const lookupKey = `${countryIso}|${zipCode}`;
    if (lastLookupKeyRef.current === lookupKey) return undefined; // AC3
    // Claimed up-front so a re-render can't fire the same lookup twice; released
    // again below if it turns out to resolve to nothing.
    lastLookupKeyRef.current = lookupKey;

    let isCurrent = true;
    lookupPostcodeLocation(countryIso, zipCode).then((result) => {
      // Only the latest response may be applied - the pair has moved on.
      if (!isCurrent) return;
      if (!result) {
        lastLookupKeyRef.current = null;
        return;
      }

      const { provinceName, cityName } = result;

      // Both fields are written unconditionally (AC4) - a half-hit must clear
      // the other field rather than leave the previous postcode's value behind
      // (AC5). Written straight through setValue rather than the Controllers'
      // onChange, so the country -> province -> city clearing cascade below
      // doesn't wipe what we just filled in.
      const provinceMatch = provinceName
        ? getProvinces(countryIso).find(
            (opt) => opt.name.toLowerCase() === provinceName.toLowerCase(),
          )
        : null;
      const nextProvinceIso = provinceMatch?.code || '';
      setValue(fieldNames.province, provinceMatch?.name || provinceName, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setProvinceIso(nextProvinceIso);

      const cityMatch = cityName
        ? (nextProvinceIso ? getCities(countryIso, nextProvinceIso) : []).find(
            (opt) => opt.name.toLowerCase() === cityName.toLowerCase(),
          )
        : null;
      setValue(fieldNames.city, cityMatch?.name || cityName, {
        shouldValidate: true,
        shouldDirty: true,
      });
    });

    return () => {
      isCurrent = false;
    };
  }, [
    countryIso,
    debouncedZipCode,
    watchedCountry,
    lookupPostcodeLocation,
    setValue,
    fieldNames,
  ]);

  // RAT-1824: "Search full address" autofill, matched against the same
  // localized-regions lists backing the cascading dropdowns below.
  const handleAddressSelected = useCallback(
    ({ line1, line2, city, stateLong, postalCode, country, businessName }) => {
      if (showLegalNameAndTax && businessName && !watch(fieldNames.legalName)) {
        setValue(fieldNames.legalName, businessName, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      if (line1) {
        setValue(fieldNames.addressLine1, line1, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      if (line2) {
        setValue(fieldNames.addressLine2, line2, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      const countryMatch = country
        ? countryOptions.find((opt) => opt.code === country)
        : null;
      const matchedCountryIso = countryMatch?.code || '';
      if (country) {
        setValue(
          fieldNames.country,
          countryMatch?.localized || countryMatch?.name || country,
          { shouldValidate: true, shouldDirty: true },
        );
        setCountryIso(matchedCountryIso);
        onCountryCodeChange?.(matchedCountryIso);
      }

      let matchedProvinceIso = '';
      if (stateLong) {
        const provinces = matchedCountryIso
          ? getProvinces(matchedCountryIso)
          : [];
        const provinceMatch = provinces.find(
          (opt) => opt.name.toLowerCase() === stateLong.toLowerCase(),
        );
        matchedProvinceIso = provinceMatch?.code || '';
        setValue(fieldNames.province, provinceMatch?.name || stateLong, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setProvinceIso(matchedProvinceIso);
      }

      if (city) {
        const cities =
          matchedCountryIso && matchedProvinceIso
            ? getCities(matchedCountryIso, matchedProvinceIso)
            : [];
        const cityMatch = cities.find(
          (opt) => opt.name.toLowerCase() === city.toLowerCase(),
        );
        setValue(fieldNames.city, cityMatch?.name || city, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      if (postalCode) {
        const formattedPostalCode = formatPostalCode(
          postalCode,
          countryMatch?.name || country,
        );
        setValue(fieldNames.zipCode, formattedPostalCode, {
          shouldValidate: true,
          shouldDirty: true,
        });
        // RAT-1931: this pair is already resolved, and from a full-address match
        // that is more precise than a postcode-only lookup - so mark it done
        // instead of letting the lookup re-fire and overwrite it.
        if (matchedCountryIso) {
          lastLookupKeyRef.current = `${matchedCountryIso}|${formattedPostalCode.trim()}`;
        }
      }
    },
    [
      countryOptions,
      setValue,
      watch,
      fieldNames,
      showLegalNameAndTax,
      onCountryCodeChange,
    ],
  );

  return (
    <Box>
      <Box mb={6}>
        <GoogleAddressAutocomplete
          labelText="Search full address"
          placeholder="Start typing a street, city or zip..."
          textColor={textColor}
          onAddressSelected={handleAddressSelected}
        />
        <Text fontSize="sm" color="gray.500" ms="4px" mt="6px">
          Picking a suggestion auto-fills address, zip code, country, province
          and city.
        </Text>
      </Box>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="24px">
        {showLegalNameAndTax && (
          <CommonInput
            type="text"
            name={fieldNames.legalName}
            labelText="Legal Name"
            textColor={textColor}
            brandStars={brandStars}
            placeholder="Enter Legal Name"
            register={register(fieldNames.legalName)}
            errors={errors?.[fieldNames.legalName]}
            isValidate={isSectionRequired}
          />
        )}

        {children}

        {showLegalNameAndTax && (
          <Box>
            <CommonInput
              type="text"
              name={fieldNames.taxId}
              labelText={taxIdLabel}
              textColor={textColor}
              brandStars={brandStars}
              placeholder="Business Number"
              register={{
                ...register(fieldNames.taxId, {
                  onChange: handleTaxNumberChanged,
                }),
              }}
              errors={errors?.[fieldNames.taxId]}
              isValidate={isSectionRequired}
              onBlur={handleTaxNumberBlur}
            />
            <TaxNumberValidationNotice
              result={taxValidation}
              isChecking={isCheckingTaxNumber}
              isConfirmed={isTaxNumberConfirmed}
              onConfirmChange={(checked) =>
                setValue(fieldNames.taxNumberConfirmed, checked, {
                  shouldDirty: true,
                })
              }
              onAdoptRegistryName={adoptRegistryLegalName}
            />
          </Box>
        )}

        {showLegalNameAndTax && (
          <CommonInput
            type="text"
            name={fieldNames.vatNumber}
            labelText="VAT Number"
            textColor={textColor}
            brandStars={brandStars}
            placeholder="VAT Number"
            register={{
              ...register(fieldNames.vatNumber, {
                onChange: handleTaxNumberChanged,
              }),
            }}
            errors={errors?.[fieldNames.vatNumber]}
            isValidate={isSectionRequired}
            onBlur={handleTaxNumberBlur}
          />
        )}

        <CommonInput
          type="text"
          name={fieldNames.addressLine1}
          labelText="Address Line 1"
          textColor={textColor}
          brandStars={brandStars}
          placeholder="Enter Address Line 1"
          register={register(fieldNames.addressLine1)}
          errors={errors?.[fieldNames.addressLine1]}
          isValidate={isSectionRequired}
        />

        <CommonInput
          type="text"
          name={fieldNames.addressLine2}
          labelText="Address Line 2 (Optional)"
          textColor={textColor}
          brandStars={brandStars}
          placeholder="Enter Address Line 2"
          register={register(fieldNames.addressLine2)}
          errors={errors?.[fieldNames.addressLine2]}
          isValidate={false}
        />

        <CommonInput
          type="text"
          name={fieldNames.zipCode}
          labelText="Zip Code"
          textColor={textColor}
          brandStars={brandStars}
          placeholder="Enter Zip Code"
          register={register(fieldNames.zipCode)}
          errors={errors?.[fieldNames.zipCode]}
          isValidate={isSectionRequired}
          onBlur={(e) =>
            setValue(
              fieldNames.zipCode,
              formatPostalCode(e.target.value, watch(fieldNames.country)),
              { shouldValidate: true },
            )
          }
        />

        <Controller
          name={fieldNames.country}
          control={control}
          render={({ field: { onChange, value } }) => (
            <CommonReactSelect
              label="Country"
              name={fieldNames.country}
              type="select"
              creatable
              options={countryOptions}
              value={resolveLegalOption(countryOptions, value)}
              onChange={(option) => {
                onChange(option?.localized || option?.name || '');
                const nextCode = option?.code || '';
                setCountryIso(nextCode);
                onCountryCodeChange?.(nextCode);
                setProvinceIso('');
                setValue(fieldNames.province, '');
                setValue(fieldNames.city, '');
              }}
              getNewOptionData={(input) => ({
                code: input,
                name: input,
                label: input,
              })}
              register={register}
              isRequired={isSectionRequired}
              error={errors?.[fieldNames.country]}
              getOptionLabel={(e) => e?.label || e?.name}
              getOptionValue={(e) => e?.code}
            />
          )}
        />

        <Controller
          name={fieldNames.province}
          control={control}
          render={({ field: { onChange, value } }) => (
            <CommonReactSelect
              label="Province"
              labelExtra={
                !isSpainProvince && (
                  <Box as="span" fontSize="xs" color="gray.500">
                    (Won&apos;t display on invoice)
                  </Box>
                )
              }
              name={fieldNames.province}
              type="select"
              creatable
              options={provinceOptions}
              value={resolveLegalOption(provinceOptions, value)}
              onChange={(option) => {
                onChange(option?.name || '');
                setProvinceIso(option?.code || '');
                setValue(fieldNames.city, '');
              }}
              getNewOptionData={(input) => ({ code: input, name: input })}
              register={register}
              isRequired={isSectionRequired}
              error={errors?.[fieldNames.province]}
              getOptionLabel={(e) => e?.name}
              getOptionValue={(e) => e?.code}
              isDisabled={!watchedCountry}
            />
          )}
        />

        <Controller
          name={fieldNames.city}
          control={control}
          render={({ field: { onChange, value } }) => (
            <CommonReactSelect
              label="City"
              name={fieldNames.city}
              type="select"
              creatable
              options={cityOptions}
              value={resolveLegalOption(cityOptions, value)}
              onChange={(option) => onChange(option?.name || '')}
              getNewOptionData={(input) => ({ name: input })}
              register={register}
              isRequired={isSectionRequired}
              error={errors?.[fieldNames.city]}
              getOptionLabel={(e) => e?.name}
              getOptionValue={(e) => e?.name}
              isDisabled={!watchedProvince}
            />
          )}
        />
      </SimpleGrid>
      {showAddressWarning && !isAddressWarningDismissed && (
        <Alert status="warning" borderRadius="md" mt={4}>
          <AlertIcon />
          <Box flex="1">
            This address may contain a typo - we couldn&apos;t find it in our
            address database. Please double-check it. If you&apos;re confident
            it&apos;s correct, you can ignore this warning.
          </Box>
          <CloseButton onClick={() => setIsAddressWarningDismissed(true)} />
        </Alert>
      )}
    </Box>
  );
};

LegalFieldsSection.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.object,
  fieldNames: PropTypes.object,
  requiredFieldNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  isRequired: PropTypes.bool,
  textColor: PropTypes.string,
  brandStars: PropTypes.string,
  taxIdLabel: PropTypes.string,
  locale: PropTypes.string,
  isSpainProvince: PropTypes.bool,
  showLegalNameAndTax: PropTypes.bool,
  initialCountryCode: PropTypes.string,
  initialProvinceCode: PropTypes.string,
  onCountryCodeChange: PropTypes.func,
  children: PropTypes.node,
};

export default LegalFieldsSection;
